<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import $ from 'jquery';
import {
    computed,
    nextTick,
    onMounted,
    provide,
    shallowRef,
    unref,
    watch,
} from 'vue';

import type {
    IdRef,
    NavTabsParent,
    TabButtonProps,
    TabPaneType,
} from './injection';
import { NavTabsParentKey } from './injection';
import TabButton from './tab-button.vue';

//valueProp, it cannot handle dynamic ones
const props = defineProps<{
    modelValue?: string;
    tabsClass?: string;
    scrollIntoView?: number;
}>();
//eslint-obsolete no-spaced-func
const emit = defineEmits<{
    (event: 'update:modelValue', value: string): void;
}>();
let activeTab: string | undefined;

const tabsElement = shallowRef<HTMLElement | undefined>();
const contentElement = shallowRef<HTMLElement | undefined>();
const panes = new Map<string, TabPaneType>();
const panesElements = new Map<HTMLElement, TabPaneType>();
const panesArray = shallowRef<TabPaneType[]>([]);
let waitingScrollIntoView = false;

function updatePanes() {
    const _contentElement = unref(contentElement);
    if(!_contentElement) return;

    const children = $(_contentElement).find('[data-auto-tab-pane]');
    const _panesArray: (TabPaneType | undefined)[] | TabPaneType[] = $.makeArray(children).map((element) => {
        const pane = panesElements.get(element);
        if(!pane) return undefined;
        return pane;
    });
    panesArray.value = _panesArray.filter((pane) => !!pane);
}

const lazyUpdatePanes = useDebounceFn(() => {
    nextTick(() => {
        updatePanes();
    });
}, 100);

const tabsParent: NavTabsParent = {
    registerPane(pane) {
        panes.set(pane.idRef.id, pane);
        panesElements.set(pane.element, pane);
        lazyUpdatePanes();
    },
    updatePane(idRef, element) {
        const paneDef = panes.get(idRef.id);
        if(paneDef && (paneDef.element !== element)) {
            panesElements.delete(paneDef.element);
            panesElements.set(element, paneDef);
            paneDef.element = element;
            lazyUpdatePanes();
        }
    },
    unregisterPane(idRef) {
        const el = panes.get(idRef.id)?.element;
        if(el) panesElements.delete(el);
        panes.delete(idRef.id);
        lazyUpdatePanes();
    },
};
provide(NavTabsParentKey, tabsParent);

function _scrollIntoView() {
    function tryScrollIntoView() {
        const _tabsElement = unref(tabsElement);
        const _tabsControl = _tabsElement && $(_tabsElement);
        const _activePane = activeTab && panes.get(activeTab);
        const _activePaneControl = _activePane ? $(`#${_activePane.idRef.tabid}`) : undefined;
        const canScrollIntoView = activeTab && _tabsControl?.is(':visible') && _activePaneControl?.is(':visible');
        if(!canScrollIntoView) {
            waitingScrollIntoView = true;
            nextTick(() => { tryScrollIntoView(); });
            return;
        }
        _activePaneControl?.get(0)?.scrollIntoView();
        waitingScrollIntoView = false;
    }
    nextTick(() => {
        if(!waitingScrollIntoView) {
            tryScrollIntoView();
        }
    });
}

function activateTab(manualActivation?: boolean) {
    let activeTabChanged = false;
    for(const pane of panes.values()) {
        //will affect buttons too
        const isActive = (pane.idRef.id === activeTab);
        if(!!isActive !== !!unref(pane.active)) {
            if(isActive) activeTabChanged = true;
            pane.active.value = isActive;
        }
    }
    if(activeTabChanged && manualActivation && (props.scrollIntoView !== undefined)) {
        _scrollIntoView();
    }
}
function onTabActivate(idRef: IdRef) {
    activeTab = idRef.id;
    activateTab();
    emit('update:modelValue', activeTab);
}

watch(() => props.modelValue, (value) => {
    if(activeTab !== value) {
        activeTab = value;
        activateTab(true);
    }
});

watch(() => props.scrollIntoView, () => {
    _scrollIntoView();
});

onMounted(() => {
    if(props.modelValue) {
        activeTab = props.modelValue;
        activateTab();
    } else {
        for(const pane of panes.values()) {
            //simulate activation for first initiallyActive
            if(unref(pane.active)) {
                onTabActivate(pane.idRef);
                break;
            }
        }
    }
});

const buttonsArray = computed(() => {
    const _panesArray = unref(panesArray);
    const _buttons = _panesArray.map((pane) => {
        const buttonProps: TabButtonProps = {
            active: unref(pane.active),
            title: pane.props.title,
            idRef: pane.idRef,
            hidden: unref(pane.hidden),
        };
        return {
            props: {
                ...buttonProps,
                key: pane.idRef.id,
            },
            on: {
                activate: onTabActivate,
            },
        };
    });
    return _buttons;
});
</script>

<template>
    <div>
        <ul ref="tabsElement" :class="props.tabsClass ? props.tabsClass : 'nav nav-pills'" role="tablist">
            <template v-for="button in buttonsArray">
                <slot name="button" :props="button.props" :on="button.on">
                    <TabButton v-bind="button.props" v-on="button.on" />
                </slot>
            </template>
        </ul>
        <div ref="contentElement" class="tab-content">
            <slot />
        </div>
    </div>
</template>
